export interface Volume {
  number: string
  title: string
  subtitle: string
  intro: string[]
  questions?: string[]
  highlight?: string
  afterHighlight?: string
  bulletsIntro: string
  bullets: string[]
  sequenceIntro?: string
  sequence?: string
  secondaryBulletsIntro?: string
  secondaryBullets?: string[]
  resultLead?: string
  resultQuote?: string
  result: string
  resultEmphasis?: string
  popular?: boolean
  popularLabel?: string
}

export const volumes: Volume[] = [
  {
    number: 'Volume 01',
    title: 'Bartending Fundamentals, Bar Tools, Glassware & Essential Techniques',
    subtitle: 'The Foundation Every Bartending Student Needs',
    intro: [
      'Every new student arrives behind the bar with the same problem: unfamiliar tools, a dozen glass shapes and no idea where anything goes.',
      'Measuring, shaking, stirring and straining look simple when you do them — and are surprisingly hard to teach with words alone.',
      'This volume turns bar fundamentals into labeled tool diagrams, station layouts, glassware charts, numbered technique sequences, ❌/✅ comparisons and drills you can use from the very first class.',
    ],
    bulletsIntro: 'Inside:',
    bullets: [
      'The bartender’s role & anatomy of a bar',
      'The workstation & mise en place',
      'Measuring, mixing & straining tools',
      'Glassware families & choosing the right glass',
      'Glass handling, care & chilling',
      'Ice types, dilution & ice safety',
      'Jigger accuracy & free pouring',
      'Building, stirring & shaking technique',
      'Shake or stir? Clear decision rules',
      'Hawthorne, julep & double straining',
      'Muddling, rolling, throwing & dry shaking',
      'Layering, floating, rinsing & dashing',
      'Citrus prep, basic syrups & garnishes',
      'Speed, efficiency & multiple-drink builds',
      'Hygiene, bar safety & responsible service basics',
      '4-week fundamentals plan & rubric',
    ],
    result: 'Give every student clean, confident technique before they make their first real cocktail.',
  },
  {
    number: 'Volume 02',
    title: 'Classic Cocktails, International Recipes & Drink Preparation',
    subtitle: 'The Classics Every Bartender Must Know',
    intro: [
      'Classic cocktails are where most students feel lost — dozens of specs, families and methods that all blur together.',
      'Without clear specs and side-by-side comparisons, the Daiquiri, the Sidecar and the Margarita become a list nobody remembers.',
      'This volume organizes the classics into spec cards with ml/oz, cocktail family maps, step-by-step builds, common mistakes and memory drills.',
    ],
    popular: true,
    popularLabel: 'CORE CLASSICS',
    bulletsIntro: 'Inside:',
    bullets: [
      'How to read a cocktail spec',
      'Balance: strong, sweet, sour, bitter & diluted',
      'The golden ratio for sours',
      'Cocktail families map',
      'Daiquiri, Whiskey Sour, Margarita & Sidecar',
      'Pisco Sour, Gimlet & Cosmopolitan',
      'Old Fashioned, Martini & Manhattan',
      'Negroni & Sazerac',
      'Mojito, Caipirinha & Moscow Mule',
      'Tom Collins, Paloma, Spritz & French 75',
      'Bloody Mary, Piña Colada & Mai Tai',
      'Espresso Martini, Irish Coffee & Mint Julep',
      'Classic zero-proof drinks',
      'Garnish rules & rims for classics',
      'Common recipe mistakes & adjusting to taste',
      'Recipe memory drills & 4-week plan',
    ],
    result: 'Help students master the classics with specs, methods and families they actually remember.',
  },
  {
    number: 'Volume 03',
    title: 'Advanced Mixology, Molecular Techniques & Creative Cocktails',
    subtitle: 'From Bartender to Mixologist',
    intro: [
      'Advanced students want more than recipes — they want to understand how flavor, texture and technique work together.',
      'Infusions, clarification, carbonation and foams are hard to teach safely until students can see every step and every safety rule on the page.',
      'This volume explains modern mixology with process diagrams, technique cards, safety notes, flavor matrices and a step-by-step method to create signature cocktails.',
    ],
    bulletsIntro: 'Inside:',
    bullets: [
      'The modern mixology mindset',
      'Flavor building blocks & balance',
      'Rich, flavored & infused syrups',
      'Oleo saccharum, shrubs & cordials',
      'Spirit infusions & fat-washing',
      'Tinctures & house bitters basics',
      'Milk-washing & clarification',
      'Carbonating cocktails',
      'Foams, airs & reverse dry shake',
      'Smoke, aroma & dry ice safety',
      'Ice program, temperature & dilution',
      'Texture, mouthfeel & kitchen techniques',
      'Zero-waste & low/no-alcohol drinks',
      'Creating a signature cocktail step by step',
      'Twisting classics & flavor pairing matrix',
      'Advanced lab rotations & 4-week plan',
    ],
    result: 'Help students create balanced, original cocktails with modern techniques taught safely.',
  },
  {
    number: 'Volume 04',
    title: 'Spirits, Liqueurs, Flavor Profiles & Sensory Analysis',
    subtitle: 'Know Every Bottle on the Back Bar',
    intro: [
      'Students pour spirits every day — and then struggle to explain how they are made or why one works better in a cocktail than another.',
      'Distillation, aging, styles and flavor profiles are hard to teach until students can see the process and compare them side by side.',
      'This volume maps the back bar with production diagrams, spirit category charts, botanical maps, aroma families, tasting grids and comparative flights.',
    ],
    bulletsIntro: 'Inside:',
    bullets: [
      'How spirits are made: fermentation & distillation',
      'Pot still vs. column still',
      'Aging, wood, ABV & proof',
      'Vodka, gin & the gin botanicals map',
      'Rum styles, tequila & mezcal',
      'Bourbon, Scotch & world whiskies',
      'Brandy, Cognac & other spirits',
      'Orange, herbal & bitter liqueurs',
      'Fortified & aromatized wines',
      'Bitters, mixers, juices & sweeteners',
      'How to nose & taste a spirit',
      'Aroma families & professional tasting grid',
      'Comparative tasting flights',
      'Choosing the right spirit for a cocktail',
      'Storage, shelf life & common myths',
      'Aroma drills & 4-week sensory plan',
    ],
    result: 'Help students understand every spirit, describe its flavor and choose it with confidence.',
  },
  {
    number: 'Volume 05',
    title: 'Cocktail Presentation, Garnishing, Menu Design & Bar Operations',
    subtitle: 'From the Garnish to the Profit Line',
    intro: [
      'Presentation is what guests see first — and operations are what keep a bar profitable night after night.',
      'Garnishes, menus, costing, inventory and peak-hour workflows are hard to teach without clear visuals and real numbers.',
      'This volume covers presentation and operations with garnish sequences, menu structures, costing worksheets, inventory methods and service workflows.',
    ],
    bulletsIntro: 'Inside:',
    bullets: [
      'Glass, ice & the wash line',
      'Garnish principles & expressed peels',
      'Twists, spirals, wheels & wedges',
      'Herb, fruit & dehydrated garnishes',
      'Rims, dusts & bitters designs',
      'Garnish prep & food safety',
      'Cocktail photography for menus',
      'Cocktail menu concept & structure',
      'Writing drink descriptions & layout',
      'Costing a cocktail & pour cost',
      'Batching, prep & seasonal menus',
      'Bar layout, workflow & stations',
      'Inventory, ordering & waste control',
      'Service workflows & peak-hour management',
      'Hospitality standards & complaints',
      'Operations simulation & 4-week plan',
    ],
    result: 'Help students present drinks beautifully and run a bar that is organized and profitable.',
  },
  {
    number: 'Volume 06',
    title: 'Bartending Instruction, Training Programs & Practical Assessments',
    subtitle: 'Teach With a System',
    intro: [
      'Knowing how to make drinks is not the same as knowing how to teach it.',
      'Instructors need clear methods for lessons, demonstrations, practice, feedback, assessments and long-term bartender development.',
      'This volume gives you lesson structures, course outlines, ready lesson plans, service simulations, exam setups, rubrics and certificates ready to use.',
    ],
    bulletsIntro: 'Inside:',
    bullets: [
      'The bartending instructor’s role',
      'How adults learn practical skills',
      'The 4-step teaching method',
      'Learning objectives & lesson design',
      'Setting up the training bar',
      'Practice with water first',
      'Demonstration & feedback skills',
      'Beginner, intermediate & advanced courses',
      'Staff onboarding program for bars',
      'Ready lesson plans: jigger, shake, sours & more',
      'Speed drills, service simulations & role-plays',
      'Skill progression ladder',
      'Practical exam design & rubrics',
      'Quiz bank & student progress tracker',
      'Public workshops & corporate training',
      '6-week course master plan & certificates',
    ],
    result: 'Plan, deliver and assess every class with structure, from first-time students to professional bartenders.',
  },
]
